import React from 'react';
import { createPortal } from 'react-dom';
import { Bot, MessageSquare, X } from 'lucide-react';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  quickQuestions: string[];
  onSuggestionClick: (suggestion: string) => void;
  onNewChat: () => void;
  isMobile?: boolean;
}

/**
 * Sidebar - Mobile drawer with portal for proper z-index
 */
export const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  onClose,
  quickQuestions,
  onSuggestionClick,
  onNewChat,
  isMobile = false,
}) => {
  const sidebar = (
    <div className={`
      fixed top-[var(--header-h)] bottom-0 left-0 z-50 w-64 bg-[rgb(var(--bg))] border-r border-[rgb(var(--border))]
      transform transition-transform duration-300 ease-in-out
      ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      lg:translate-x-0 lg:z-auto
    `}>
        <div className="flex flex-col h-full">
          {/* Sidebar Header */}
          <div className="p-4 border-b border-[rgb(var(--border))]">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-br from-[#9c68f2] to-[#422ca5] rounded-lg flex items-center justify-center">
                  <Bot className="w-5 h-5 text-[rgb(var(--fg))]" />
                </div>
                <span className="font-semibold text-[rgb(var(--fg))]">Fiverflow AI</span>
              </div>
              <button
                onClick={onClose}
                className="lg:hidden p-1.5 hover:bg-[rgb(var(--card))] rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Close sidebar"
              >
                <X className="w-5 h-5 text-[rgb(var(--muted))]" />
              </button>
            </div>
          </div>

          {/* New Chat Button */}
          <div className="p-4">
            <button
              onClick={onNewChat}
              className="w-full flex items-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-[#9c68f2] to-[#422ca5] text-[rgb(var(--fg))] rounded-lg hover:from-[#8655e6] hover:to-[#3a2694] transition-all shadow-sm min-h-[44px]"
            >
              <MessageSquare className="w-4 h-4" />
              <span className="font-medium">New Chat</span>
            </button>
          </div>

          {/* Quick Questions */}
          <div className="flex-1 overflow-y-auto px-4 pb-4">
            <div className="space-y-2">
              <p className="text-xs font-semibold text-[rgb(var(--muted))] uppercase tracking-wide px-2 mb-2">
                Quick Questions
              </p>
              {quickQuestions.map((question, index) => (
                <button
                  key={index}
                  onClick={() => {
                    onSuggestionClick(question);
                    onClose();
                  }}
                  className="w-full text-left px-3 py-2.5 text-sm text-[rgb(var(--fg))] hover:bg-[rgb(var(--card))] rounded-lg transition-colors min-h-[44px]"
                >
                  {question}
                </button>
              ))}
            </div>
          </div>
        </div>
    </div>
  );

  const overlay = isOpen && (
    <div
      className="fixed inset-0 bg-black/50 z-40 lg:hidden"
      onClick={onClose}
      aria-hidden="true"
    />
  );

  // Render overlay with portal on mobile for proper z-index
  if (typeof window !== 'undefined' && isMobile && overlay) {
    return (
      <>
        {sidebar}
        {createPortal(overlay, document.body)}
      </>
    );
  }

  return (
    <>
      {sidebar}
      {overlay}
    </>
  );
};

